import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { OperationAction } from '@Constants/operations';
import { shiftReportKeys } from '@Hooks/useShiftReports';

interface Props {
  title: string;
  subtitle?: string;
  count?: number;
  soulId?: string;
  actions?: OperationAction[];
  children: ReactNode;
}

export default function OperationBlock({
  title,
  subtitle,
  count,
  soulId,
  actions,
  children,
}: Props) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const handleSuccess = () => {
    if (!soulId) return;
    queryClient.invalidateQueries({ queryKey: shiftReportKeys.forSoul(soulId) });
  };

  const handleRun = async (action: OperationAction) => {
    if (!soulId || !action.run) return;
    setLoading((prev) => ({ ...prev, [action.id]: true }));
    try {
      await action.run(soulId);
      toastSuccess(`${action.label} triggered.`);
      handleSuccess();
    } catch (e: any) {
      toastError(`${action.label} failed: ${e?.message ?? e}`);
    } finally {
      setLoading((prev) => ({ ...prev, [action.id]: false }));
    }
  };

  return (
    <section className="border-t border-zinc-200 pt-6">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Heading level="h3">{title}</Heading>
          {typeof count === 'number' && (
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">
              {count}
            </span>
          )}
        </div>
        {actions?.length ? (
          <div className="flex items-center gap-2">
            {actions.map((action) =>
              action.Trigger && soulId ? (
                // Custom trigger (e.g. date-picker popover)
                <action.Trigger
                  key={action.id}
                  soulId={soulId}
                  onSuccess={handleSuccess}
                />
              ) : (
                // Plain button fallback
                <Button
                  key={action.id}
                  variant="outline"
                  size="sm"
                  loading={loading[action.id]}
                  onClick={() => handleRun(action)}
                  title={action.description}
                >
                  {action.label}
                </Button>
              )
            )}
          </div>
        ) : null}
      </div>
      {subtitle && <p className="mb-4 text-xs text-zinc-500">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      {children}
    </section>
  );
}
